import { PropTypes } from 'typed/imports';
import {
    GoalMetricValueType, PeriodType, StrategicGoalMilestoneAchievability, StrategicGoalMilestoneStatus, StrategicProgramRiskLevel, StrategicProgramStatus,
    StrategyDescriptionStatus, StrategyExecutionSecondaryStatusUpdateType, StrategyNamingType, StrategyProgressCalculationMethod, StrategyProgressCalculationSource, StrategyPublishingType, StrategyView,
    defaults, entities
} from 'typed/api';
import { Period, PeriodRange, deepAssign, deepDiff, omit } from 'typed/utils';
import { Dateable } from 'typed/utils/Period';

const
    MILESTONE_ENTITY_TYPE = defaults.StrategicGoalMilestoneReport.EntityType,
    PROGRAM_ENTITY_TYPE = defaults.StrategicProgramReport.EntityType,

    MONTH_COUNT_IN_TERTIAL_PERIOD = 4,
    MONTH_COUNT_IN_QUARTER_PERIOD = 3,
    MONTH_COUNT_IN_YEAR = 12;

export const DEFAULT_TERTIAL_PERIOD_START_MONTH = 1;
export const DEFAULT_QUARTER_PERIOD_START_MONTH = 1;

export const TEAMS_AND_USERS_STRATEGY_ID = -1;
export const OTHER_ITEMS_GOAL_ID = -1;

export enum StrategyMindmapNodeType {
    STRATEGY = 'STRATEGY',
    DESCRIPTION = 'DESCRIPTION',
    SELECTION = 'SELECTION',
    DASHBOARD = 'DASHBOARD',
    MEMO = 'MEMO',
    ATTACHMENT = 'ATTACHMENT',
    GOAL = 'GOAL',
    CRITICAL_SUCCESS_FACTOR = 'CRITICAL_SUCCESS_FACTOR',
    METRIC = 'METRIC',
    MILESTONE = 'MILESTONE',
    PROGRAM = 'PROGRAM',
    TEAM = 'TEAM',
    LINKED_TEAM = 'LINKED_TEAM',
    LINKED_VIRTUAL_TEAM = 'LINKED_VIRTUAL_TEAM',
    INITIATIVE = 'INITIATIVE',
    WORKSHOP = 'WORKSHOP',
    CUSTOM_ENTITY_DATA = 'CUSTOM_ENTITY_DATA'
}

export enum StrategyRoadmapHierarchyType {
    STRATEGY = 1,
    ORGANIZATIONS = 2
}

export enum StrategyRoadmapPeriodType {
    WEEK = PeriodType.WEEK,
    MONTH = PeriodType.MONTH,
    QUARTER = PeriodType.QUARTER,
    TERTIAL = 20,
    YEAR = PeriodType.YEAR
}

export enum StrategyRoadmapLayoutType {
    ROW = 1,
    CARD = 2
}

export enum StrategyBoardScopeType {
    ME = 1,
    TEAM = 2
}

export enum StrategyBoardContentAreaType {
    ACTIVITIES = 1,
    STATUS_UPDATES = 2,
    WORKSHOPS = 3,
    MEETINGS = 4,
    SCOREBOARDS = 5,
    PULSE = 6
}

export interface StrategyCustomFieldDateFilter {
    CustomFieldID: number;
    CustomPeriodStart: Date | null;
    CustomPeriodEnd: Date | null;
}

export interface StrategyFilter {
    View: StrategyView;

    StrategyIDs: number[];
    ResponsibleCompanyFunctionIDs: number[];
    ResponsibleVirtualTeamIDs: number[];
    ResponsibleUserIDs: number[];

    TextFilter: string;
    AchievabilityIDs: (StrategicGoalMilestoneAchievability | null)[];
    RiskLevelIDs: (StrategicProgramRiskLevel | null)[];
    ShowOnlyPersonalDrafts: boolean;
    ShowOnlyOthersDrafts: boolean;
    StrategyStatusIDs: StrategyDescriptionStatus[];

    MilestoneCompanyFunctionIDs: number[];
    MilestoneVirtualTeamIDs: number[];
    MilestoneStatusIDs: StrategicGoalMilestoneStatus[];
    MilestoneCustomFieldOptionIDs: number[];
    MilestoneCustomFieldDateFilters: StrategyCustomFieldDateFilter[];

    ProgramCompanyFunctionIDs: number[];
    ProgramVirtualTeamIDs: number[];
    ProgramStatusIDs: StrategicProgramStatus[];
    ProgramCustomFieldOptionIDs: number[];
    ProgramCustomFieldDateFilters: StrategyCustomFieldDateFilter[];

    CustomPeriodStart: Date | null;
    CustomPeriodEnd: Date | null;

    ObjectiveNotUpdatedPeriodStart: Date | null;
    ObjectiveNotUpdatedPeriodEnd: Date | null;
    ActionNotUpdatedPeriodStart: Date | null;
    ActionNotUpdatedPeriodEnd: Date | null;
    [key: string]: any;
}

export interface StrategyContentSettings {
    ShowTeamConnections: boolean;
    ShowIdealState: boolean;
    ShowMetrics: boolean;
    ShowCriticalSuccessFactors: boolean;

    HierarchyTypeID: StrategyRoadmapHierarchyType;
    PeriodTypeID: StrategyRoadmapPeriodType;
    LayoutTypeID: StrategyRoadmapLayoutType;
    DragAllTimelines: boolean;

    OnlyDirectlyConnectedKanbanItems: boolean;
    ShowKanbanActions: boolean;

    CollapsedCompanyFunctionIDs: number[];
    CollapsedVirtualTeamIDs: number[];
    CollapsedStrategyIDs: number[];
    CollapsedGoalIDs: number[];
    ExpandedMilestoneIDs: number[];
    ExpandedProgramIDs: number[];
}

export interface StrategyActionListContentSettings {
    initialized: boolean;
    strategyIDs: number[];
    goalIDs: number[];
    milestoneIDs: number[];
    programIDs: number[];
}

export interface StrategyPlannerFilter {
    DarkMode: boolean;
    HorizontalLayout: boolean;
    GroupByTeams: boolean;
    ColorByTeams: boolean;
    ColorBySuccess: boolean;
    HiddenNodeTypes: StrategyMindmapNodeType[];
    HiddenStrategyIDs: number[];
    HiddenGoalIDs: number[];
    HiddenMilestoneIDs: number[];
    HiddenProgramIDs: number[];
    ShowCustomEntities: boolean;
}

export interface StrategyOrganizationStructureFilter {
    DarkMode: boolean;
    HorizontalLayout: boolean;
    GroupByTeams: boolean;
    GroupBySuccess: boolean;
    ShowActions: boolean;
    ShowEmptyNodes: boolean;
    ShowConnectedTeams: boolean;
}

export interface BusinessReviewSettings {
    CurrentPeriodStartDate: Date | null;
}

const StrategyCustomFieldDateFilterPropType = PropTypes.shape({
    CustomFieldID: PropTypes.number,
    CustomPeriodStart: PropTypes.instanceOf(Date),
    CustomPeriodEnd: PropTypes.instanceOf(Date)
});

export const StrategyFilterPropType = PropTypes.shape({
    View: PropTypes.oneOf(Object.values(StrategyView).filter(v => +v)),

    StrategyID: PropTypes.arrayOf(PropTypes.number),
    ResponsibleCompanyFunctionIDs: PropTypes.arrayOf(PropTypes.number),
    ResponsibleVirtualTeamIDs: PropTypes.arrayOf(PropTypes.number),
    ResponsibleUserIDs: PropTypes.arrayOf(PropTypes.number),

    TextFilter: PropTypes.string,
    AchievabilityIDs: PropTypes.arrayOf(PropTypes.number),
    RiskLevelIDs: PropTypes.arrayOf(PropTypes.number),
    ShowOnlyPersonalDrafts: PropTypes.bool,
    ShowOnlyOthersDrafts: PropTypes.bool,
    StrategyStatusIDs: PropTypes.arrayOf(PropTypes.number),

    MilestoneCompanyFunctionIDs: PropTypes.arrayOf(PropTypes.number),
    MilestoneVirtualTeamIDs: PropTypes.arrayOf(PropTypes.number),
    MilestoneStatusIDs: PropTypes.arrayOf(PropTypes.number),
    MilestoneCustomFieldOptionIDs: PropTypes.arrayOf(PropTypes.number),
    MilestoneCustomFieldDateFilters: PropTypes.arrayOf(StrategyCustomFieldDateFilterPropType),

    ProgramCompanyFunctionIDs: PropTypes.arrayOf(PropTypes.number),
    ProgramVirtualTeamIDs: PropTypes.arrayOf(PropTypes.number),
    ProgramStatusIDs: PropTypes.arrayOf(PropTypes.number),
    ProgramCustomFieldOptionIDs: PropTypes.arrayOf(PropTypes.number),
    ProgramCustomFieldDateFilters: PropTypes.arrayOf(StrategyCustomFieldDateFilterPropType),

    CustomPeriodStart: PropTypes.instanceOf(Date),
    CustomPeriodEnd: PropTypes.instanceOf(Date)
});

export const StrategyContentSettingsPropType = PropTypes.shape({
    ShowTeamConnections: PropTypes.bool,
    ShowIdealState: PropTypes.bool,
    ShowMetrics: PropTypes.bool,
    ShowCriticalSuccessFactors: PropTypes.bool,

    HierarchyTypeID: PropTypes.oneOf(Object.values(StrategyRoadmapHierarchyType)),
    PeriodTypeID: PropTypes.oneOf(Object.values(StrategyRoadmapPeriodType)),
    LayoutTypeID: PropTypes.oneOf(Object.values(StrategyRoadmapLayoutType)),
    DragAllTimelines: PropTypes.bool,

    OnlyDirectlyConnectedKanbanItems: PropTypes.bool,
    ShowKanbanActions: PropTypes.bool,

    CollapsedCompanyFunctionIDs: PropTypes.arrayOf(PropTypes.number),
    CollapsedVirtualTeamIDs: PropTypes.arrayOf(PropTypes.number),
    CollapsedStrategyIDs: PropTypes.arrayOf(PropTypes.number),
    CollapsedGoalIDs: PropTypes.arrayOf(PropTypes.number),
    ExpandedMilestoneIDs: PropTypes.arrayOf(PropTypes.number)
});

export const StrategyActionListContentSettingsPropType = PropTypes.shape({
    initialized: PropTypes.bool,
    strategyIDs: PropTypes.arrayOf(PropTypes.number),
    goalIDs: PropTypes.arrayOf(PropTypes.number),
    milestoneIDs: PropTypes.arrayOf(PropTypes.number),
    programIDs: PropTypes.arrayOf(PropTypes.number)
});

export const StrategyPlannerFilterPropType = PropTypes.shape({
    DarkMode: PropTypes.bool,
    HorizontalLayout: PropTypes.bool,
    ColorByTeams: PropTypes.bool,
    GroupBySuccess: PropTypes.bool,
    HiddenNodeTypes: PropTypes.arrayOf(PropTypes.string),
    HiddenStrategyIDs: PropTypes.arrayOf(PropTypes.number),
    HiddenGoalIDs: PropTypes.arrayOf(PropTypes.number),
    HiddenMilestoneIDs: PropTypes.arrayOf(PropTypes.number),
    HiddenProgramIDs: PropTypes.arrayOf(PropTypes.number),
    ShowCustomEntities: PropTypes.bool
});

export const StrategyOrganizationStructureFilterPropType = PropTypes.shape({
    DarkMode: PropTypes.bool,
    HorizontalLayout: PropTypes.bool,
    GroupByTeams: PropTypes.bool,
    GroupBySuccess: PropTypes.bool,
    ShowActions: PropTypes.bool,
    ShowEmptyNodes: PropTypes.bool,
    ShowConnectedTeams: PropTypes.bool
});

export const BusinessReviewSettingsPropType = PropTypes.shape({
    CurrentPeriodStartDate: PropTypes.instanceOf(Date)
});


export const DefaultStrategyFilter: StrategyFilter = {
    View: StrategyView.ROADMAP,

    StrategyIDs: [],
    ResponsibleCompanyFunctionIDs: [],
    ResponsibleVirtualTeamIDs: [],
    ResponsibleUserIDs: [],

    TextFilter: '',
    AchievabilityIDs: [],
    RiskLevelIDs: [],
    ShowOnlyPersonalDrafts: true,
    ShowOnlyOthersDrafts: false,
    StrategyStatusIDs: [StrategyDescriptionStatus.PLANNING, StrategyDescriptionStatus.ACTIVE],

    MilestoneCompanyFunctionIDs: [],
    MilestoneVirtualTeamIDs: [],
    MilestoneStatusIDs: [StrategicGoalMilestoneStatus.DRAFT, StrategicGoalMilestoneStatus.ACTIVE, StrategicGoalMilestoneStatus.COMPLETED],
    MilestoneCustomFieldOptionIDs: [],
    MilestoneCustomFieldDateFilters: [],

    ProgramCompanyFunctionIDs: [],
    ProgramVirtualTeamIDs: [],
    ProgramStatusIDs: [StrategicProgramStatus.DRAFT, StrategicProgramStatus.OPEN],
    ProgramCustomFieldOptionIDs: [],
    ProgramCustomFieldDateFilters: [],

    CustomPeriodStart: null,
    CustomPeriodEnd: null
};

export const DefaultStrategyContentSettings: StrategyContentSettings = {
    ShowTeamConnections: false,
    ShowIdealState: false,
    ShowMetrics: true,
    ShowCriticalSuccessFactors: false,

    HierarchyTypeID: StrategyRoadmapHierarchyType.STRATEGY,
    PeriodTypeID: StrategyRoadmapPeriodType.QUARTER,
    LayoutTypeID: StrategyRoadmapLayoutType.ROW,
    DragAllTimelines: false,

    OnlyDirectlyConnectedKanbanItems: false,
    ShowKanbanActions: false,

    CollapsedCompanyFunctionIDs: [],
    CollapsedVirtualTeamIDs: [],
    CollapsedStrategyIDs: [],
    CollapsedGoalIDs: [],
    ExpandedMilestoneIDs: [],
    ExpandedProgramIDs: []
};

export const DefaultStrategyActionListContentSettings: StrategyActionListContentSettings = {
    initialized: false,
    strategyIDs: [],
    goalIDs: [],
    milestoneIDs: [],
    programIDs: []
};

export const DefaultStrategyPlannerFilter: StrategyPlannerFilter = {
    DarkMode: false,
    HorizontalLayout: true,
    GroupByTeams: false,
    ColorByTeams: false,
    ColorBySuccess: false,
    HiddenNodeTypes: [
        StrategyMindmapNodeType.METRIC,
        StrategyMindmapNodeType.INITIATIVE,
        StrategyMindmapNodeType.DASHBOARD,
        StrategyMindmapNodeType.LINKED_TEAM,
        StrategyMindmapNodeType.LINKED_VIRTUAL_TEAM
    ],
    HiddenStrategyIDs: [],
    HiddenGoalIDs: [],
    HiddenMilestoneIDs: [],
    HiddenProgramIDs: [],
    ShowCustomEntities: false
};

export const DefaultStrategyOrganizationStructureFilter: StrategyOrganizationStructureFilter = {
    DarkMode: false,
    HorizontalLayout: false,
    GroupByTeams: false,
    GroupBySuccess: false,
    ShowActions: true,
    ShowEmptyNodes: false,
    ShowConnectedTeams: false
};

export const DefaultBusinessReviewSettings: BusinessReviewSettings = {
    CurrentPeriodStartDate: null
};


// returns number of tertial period in a year (starting from 1)
// periodStartMonth starts from 1 (1 = January)
export function getDateUTCTertial(date?: Dateable, periodStartMonth?: number): number {
    const
        periodTypeID = PeriodType.MONTH,
        datePeriod = new Period(periodTypeID, date),
        dateMonth = datePeriod.getUTCMonth() + 1, // number of month, starting from 1
        periodsCount = MONTH_COUNT_IN_YEAR / MONTH_COUNT_IN_TERTIAL_PERIOD,
        startMonth = periodStartMonth || DEFAULT_TERTIAL_PERIOD_START_MONTH;

    return dateMonth < startMonth ?
        periodsCount - Math.floor((startMonth - dateMonth - 1) / MONTH_COUNT_IN_TERTIAL_PERIOD) :
        Math.floor((dateMonth - startMonth) / MONTH_COUNT_IN_TERTIAL_PERIOD) + 1;
}

// returns number of quarter period in a year (starting from 1)
// periodStartMonth starts from 1 (1 = January)
export function getDateUTCQuarter(date?: Dateable, periodStartMonth?: number): number {
    const
        periodTypeID = PeriodType.MONTH,
        datePeriod = new Period(periodTypeID, date),
        dateMonth = datePeriod.getUTCMonth() + 1, // number of month, starting from 1
        periodsCount = MONTH_COUNT_IN_YEAR / MONTH_COUNT_IN_QUARTER_PERIOD,
        startMonth = periodStartMonth || DEFAULT_QUARTER_PERIOD_START_MONTH;

    return dateMonth < startMonth ?
        periodsCount - Math.floor((startMonth - dateMonth - 1) / MONTH_COUNT_IN_QUARTER_PERIOD) :
        Math.floor((dateMonth - startMonth) / MONTH_COUNT_IN_QUARTER_PERIOD) + 1;
}

export function getRoadmapPeriodStartPeriod(roadmapPeriodTypeID: StrategyRoadmapPeriodType, date?: Dateable, generalSettings?: entities.GeneralSettings): Period {
    let
        periodTypeID = undefined as unknown as PeriodType,
        periodNumber = -1,
        datePeriod = undefined as unknown as Period,
        startMonth = -1,
        startMonthIndex = -1;

    switch (roadmapPeriodTypeID) {
        case StrategyRoadmapPeriodType.WEEK:
        case StrategyRoadmapPeriodType.MONTH:
        case StrategyRoadmapPeriodType.YEAR:
            periodTypeID = roadmapPeriodTypeID as any as PeriodType;
            return new Period(periodTypeID, date);

        case StrategyRoadmapPeriodType.QUARTER:
            periodTypeID = PeriodType.MONTH;
            startMonth = generalSettings?.StrategyQuarterPeriodStartMonth || DEFAULT_QUARTER_PERIOD_START_MONTH;
            periodNumber = getDateUTCQuarter(date, startMonth);
            startMonthIndex = startMonth - 1 + (periodNumber - 1) * MONTH_COUNT_IN_QUARTER_PERIOD;

            if (startMonthIndex > MONTH_COUNT_IN_YEAR) {
                startMonthIndex -= MONTH_COUNT_IN_YEAR;
            }

            datePeriod = new Period(periodTypeID, date);

            if (startMonthIndex > datePeriod.getUTCMonth()) {
                datePeriod = datePeriod.setUTCFullYear(datePeriod.getUTCFullYear() - 1, true);
            }

            return datePeriod.setUTCMonth(startMonthIndex, true);

        case StrategyRoadmapPeriodType.TERTIAL:
            periodTypeID = PeriodType.MONTH;
            startMonth = generalSettings?.StrategyTertialPeriodStartMonth || DEFAULT_TERTIAL_PERIOD_START_MONTH;
            periodNumber = getDateUTCTertial(date, startMonth);
            startMonthIndex = startMonth - 1 + (periodNumber - 1) * MONTH_COUNT_IN_TERTIAL_PERIOD;

            if (startMonthIndex > MONTH_COUNT_IN_YEAR) {
                startMonthIndex -= MONTH_COUNT_IN_YEAR;
            }

            datePeriod = new Period(periodTypeID, date);

            if (startMonthIndex > datePeriod.getUTCMonth()) {
                datePeriod = datePeriod.setUTCFullYear(datePeriod.getUTCFullYear() - 1, true);
            }

            return datePeriod.setUTCMonth(startMonthIndex, true);

        default:
            throw new Error(`Unknown StrategyRoadmapPeriodType type ${roadmapPeriodTypeID}`);
    }
}

export function addPeriodsToRoadmapPeriod(roadmapPeriodTypeID: StrategyRoadmapPeriodType, period: Period, periodsCount: number): Period {
    switch (roadmapPeriodTypeID) {
        case StrategyRoadmapPeriodType.WEEK:
        case StrategyRoadmapPeriodType.MONTH:
        case StrategyRoadmapPeriodType.YEAR:
            return period.add(periodsCount);

        case StrategyRoadmapPeriodType.QUARTER:
            return period.add(periodsCount * MONTH_COUNT_IN_QUARTER_PERIOD);

        case StrategyRoadmapPeriodType.TERTIAL:
            return period.add(periodsCount * MONTH_COUNT_IN_TERTIAL_PERIOD);

        default:
            throw new Error(`Unknown StrategyRoadmapPeriodType type ${roadmapPeriodTypeID}`);
    }
}

export function getRoadmapPeriodRange(roadmapPeriodTypeID: StrategyRoadmapPeriodType, startDate: Dateable, endDate?: Dateable,
    periodsBefore?: number, periodsAfter?: number, generalSettings?: entities.GeneralSettings): PeriodRange {

    endDate = endDate || startDate;
    periodsBefore = periodsBefore || 0;
    periodsAfter = periodsAfter || 0;

    let
        periodTypeID = undefined as unknown as PeriodType,
        startPeriod = undefined as unknown as Period,
        endPeriod = undefined as unknown as Period;

    switch (roadmapPeriodTypeID) {
        case StrategyRoadmapPeriodType.WEEK:
        case StrategyRoadmapPeriodType.MONTH:
        case StrategyRoadmapPeriodType.YEAR:
            periodTypeID = roadmapPeriodTypeID as any as PeriodType;
            startPeriod = new Period(periodTypeID, startDate).add(-periodsBefore);
            endPeriod = new Period(periodTypeID, endDate).add(periodsAfter);
            break;

        case StrategyRoadmapPeriodType.QUARTER:
        case StrategyRoadmapPeriodType.TERTIAL:
            periodTypeID = PeriodType.MONTH;

            const
                startDatePeriod = getRoadmapPeriodStartPeriod(roadmapPeriodTypeID, startDate, generalSettings),
                endDatePeriod = getRoadmapPeriodStartPeriod(roadmapPeriodTypeID, endDate, generalSettings);

            startPeriod = addPeriodsToRoadmapPeriod(roadmapPeriodTypeID, startDatePeriod, -periodsBefore);
            endPeriod = addPeriodsToRoadmapPeriod(roadmapPeriodTypeID, endDatePeriod, periodsAfter);

            break;

        default:
            throw new Error(`Unknown StrategyRoadmapPeriodType type ${roadmapPeriodTypeID}`);
    }

    return new PeriodRange(periodTypeID, startPeriod, endPeriod);
}

export function getRoadmapPeriodStartDate(roadmapPeriodTypeID: StrategyRoadmapPeriodType, period?: Period, generalSettings?: entities.GeneralSettings): Date {
    const periodStart = getRoadmapPeriodStartPeriod(roadmapPeriodTypeID, period ? +period : undefined, generalSettings);
    return new Date(+periodStart);
}

export function getRoadmapPeriodEndDate(roadmapPeriodTypeID: StrategyRoadmapPeriodType, period: Period): Date {
    const nextPeriod = addPeriodsToRoadmapPeriod(roadmapPeriodTypeID, period, 1);
    return new Date(nextPeriod.valueOf() - 1000);
}

export function getRoadmapPeriodTicks(roadmapPeriodTypeID: StrategyRoadmapPeriodType, range: PeriodRange, generalSettings?: entities.GeneralSettings): Date[] {
    switch (roadmapPeriodTypeID) {
        case StrategyRoadmapPeriodType.WEEK:
        case StrategyRoadmapPeriodType.MONTH:
        case StrategyRoadmapPeriodType.YEAR:
            return range.map(tick => new Date(+tick));

        case StrategyRoadmapPeriodType.QUARTER:
        case StrategyRoadmapPeriodType.TERTIAL:
            const
                startMonth = roadmapPeriodTypeID === StrategyRoadmapPeriodType.QUARTER ?
                    generalSettings?.StrategyQuarterPeriodStartMonth || DEFAULT_QUARTER_PERIOD_START_MONTH :
                    generalSettings?.StrategyTertialPeriodStartMonth || DEFAULT_TERTIAL_PERIOD_START_MONTH,

                monthCountInPeriod = roadmapPeriodTypeID === StrategyRoadmapPeriodType.QUARTER ?
                    MONTH_COUNT_IN_QUARTER_PERIOD :
                    MONTH_COUNT_IN_TERTIAL_PERIOD;

            return range
                .map((tick) => {
                    const
                        tickDate = new Date(+tick),
                        monthIndex = tickDate.getUTCMonth() - startMonth + 1;

                    return monthIndex % monthCountInPeriod === 0 ? tickDate : null;
                })
                .filter(tick => !!tick)
                .map(tick => tick as any as Date)
                .map(tick => new Date(+tick));

        default:
            throw new Error(`Unknown StrategyRoadmapPeriodType type ${roadmapPeriodTypeID}`);
    }
}

export function convertStrategyFilterToQuery(filter: StrategyFilter, alwaysIncludeStringProperties = false): any { 
    const
        defaultFilter = omit(DefaultStrategyFilter, 'View'),
        changedProperties = deepDiff(defaultFilter, filter) || {},
        result: { [key: string]: any; } = {};

    for (const propertyName in defaultFilter) { 
        if (Object.prototype.hasOwnProperty.call(defaultFilter, propertyName) && (
            Object.prototype.hasOwnProperty.call(changedProperties, propertyName) ||
            Array.isArray(defaultFilter[propertyName]) ||
            typeof defaultFilter[propertyName] === 'boolean' ||
            alwaysIncludeStringProperties && (typeof defaultFilter[propertyName] === 'string' || defaultFilter[propertyName] instanceof String)
        )) { 
            const queryParameter = propertyName.toLowerCase()[0] + propertyName.slice(1);
            if (propertyName === 'ObjectiveNotUpdatedPeriodStart') {
                const baseQueryParameter = queryParameter.replace('ObjectiveNotUpdatedPeriodStart', ''); 
                result[`${baseQueryParameter}ObjectiveNotUpdatedPeriodStart`] = String(+filter[propertyName]); 
            }
            if (propertyName === 'MilestoneCustomFieldDateFilters' || propertyName === 'ProgramCustomFieldDateFilters') {
                (filter[propertyName] || []).forEach((value) => {
                    const baseQueryParameter = queryParameter.replace('FieldDateFilters', '');

                    if (value.CustomPeriodStart) {
                        result[`${baseQueryParameter}StartDate${value.CustomFieldID}`] = String(+value.CustomPeriodStart);
                    }

                    if (value.CustomPeriodEnd) {
                        result[`${baseQueryParameter}EndDate${value.CustomFieldID}`] = String(+value.CustomPeriodEnd);
                    }
                });
            }
            else {  
                result[queryParameter] = !Array.isArray(defaultFilter[propertyName]) ? filter[propertyName] : (filter[propertyName] || []).map((v: any) => String(v));
            }
        }
    }

    return result;
}

export function extractStrategyFilterFromQuery(query: any): StrategyFilter {
    const defaultFilter = DefaultStrategyFilter;
    let result = defaultFilter;

    for (const propertyName in defaultFilter) {
        if (Object.prototype.hasOwnProperty.call(defaultFilter, propertyName)) {
            const
                queryParameter = propertyName.toLowerCase()[0] + propertyName.slice(1),
                defaultValue = defaultFilter[propertyName];

            let value = query[queryParameter];

            if (propertyName.endsWith('CustomFieldDateFilters')) {
                const
                    entityName = propertyName.replace('CustomFieldDateFilters', ''),
                    regex = new RegExp(`^${entityName}Custom(Start|End)Date(\\d+)$`, 'i');

                value = result[propertyName].copy();

                for (const parameter in query) {
                    if (Object.prototype.hasOwnProperty.call(query, parameter) && regex.test(parameter)) {
                        const
                            match = parameter.match(regex) || [],
                            periodPropertyName = (match[1] || '').toLowerCase() === 'start' ? 'CustomPeriodStart' : 'CustomPeriodEnd',
                            entityID = +match[2],

                            propertyValue = query[parameter],
                            date = +new Date(+propertyValue) ? new Date(+propertyValue) : null;

                        if (entityID) {
                            const field = value.find((field: any) => field.CustomFieldID === entityID) || {
                                CustomFieldID: entityID,
                                CustomPeriodStart: null,
                                CustomPeriodEnd: null
                            };
                            let entityIndex = value.findIndex((field: any) => field.CustomFieldID === entityID);
                            if (entityIndex < 0) { entityIndex = value.length; }

                            value = deepAssign(value, {
                                [entityIndex]: { ...field, [periodPropertyName]: date }
                            });
                        }
                    }
                }
            }
            else if (propertyName.startsWith('CustomPeriod')) {
                value = value ? new Date(value) : null;
                if (isNaN(value)) { value = null; }
            }
            else if (Array.isArray(defaultValue)) {
                value = value ? [value].flatten().map((v: string) => v === String(null) ? null : +v).filter((v: number | null) => !!v || v === null) : [];
            }
            else if (typeof defaultValue === 'boolean') {
                value = value === 'true';
            }
            else if (!value) {
                value = defaultValue;
            }

            result = deepAssign(result, { [propertyName]: value });
        }
    }

    return deepAssign(result, {
        MilestoneCustomFieldDateFilters: result.MilestoneCustomFieldDateFilters.filter(({ CustomPeriodStart, CustomPeriodEnd }) => CustomPeriodStart || CustomPeriodEnd),
        ProgramCustomFieldDateFilters: result.ProgramCustomFieldDateFilters.filter(({ CustomPeriodStart, CustomPeriodEnd }) => CustomPeriodStart || CustomPeriodEnd)
    });
}

export function convertJsonToStrategyFilter(filterJson: string): StrategyFilter {
    const defaultFilter = DefaultStrategyFilter;
    if (!filterJson || !filterJson.length) { return defaultFilter; }

    const filter = JSON.parse(filterJson) as any as StrategyFilter;

    filter.CustomPeriodStart = filter.CustomPeriodStart ? new Date(filter.CustomPeriodStart) : null;
    filter.CustomPeriodEnd = filter.CustomPeriodEnd ? new Date(filter.CustomPeriodEnd) : null;

    filter.MilestoneCustomFieldDateFilters = (filter.MilestoneCustomFieldDateFilters || []).map(dateFilter => ({
        ...dateFilter,
        CustomPeriodStart: dateFilter.CustomPeriodStart ? new Date(dateFilter.CustomPeriodStart) : null,
        CustomPeriodEnd: dateFilter.CustomPeriodEnd ? new Date(dateFilter.CustomPeriodEnd) : null
    }));

    filter.ProgramCustomFieldDateFilters = (filter.ProgramCustomFieldDateFilters || []).map(dateFilter => ({
        ...dateFilter,
        CustomPeriodStart: dateFilter.CustomPeriodStart ? new Date(dateFilter.CustomPeriodStart) : null,
        CustomPeriodEnd: dateFilter.CustomPeriodEnd ? new Date(dateFilter.CustomPeriodEnd) : null
    }));

    if (filter.ShowOnlyPersonalDrafts === null) {
        filter.ShowOnlyPersonalDrafts = false;
        filter.ShowOnlyOthersDrafts = false;
    }

    if (filter.ShowOnlyOthersDrafts === undefined) {
        const showDrafts = (filter.StrategyStatusIDs || []).includes(StrategyDescriptionStatus.PLANNING);
        filter.ShowOnlyOthersDrafts = showDrafts && !filter.ShowOnlyPersonalDrafts;
    }

    let resultFilter = defaultFilter;
    for (const propertyName in resultFilter) {
        if (Object.prototype.hasOwnProperty.call(resultFilter, propertyName) &&
            Object.prototype.hasOwnProperty.call(filter, propertyName)) {
            resultFilter = deepAssign(resultFilter, { [propertyName]: (filter as any)[propertyName] });
        }
    }

    return resultFilter;
}

export function convertJsonToStrategyContentSettings(filterJson: string): StrategyContentSettings {
    const defaultFilter = DefaultStrategyContentSettings;
    if (!filterJson || !filterJson.length) { return defaultFilter; }

    const filter = JSON.parse(filterJson) as any;

    return deepAssign(defaultFilter, {
        ShowTeamConnections: filter.ShowTeamConnections || defaultFilter.ShowTeamConnections,
        ShowIdealState: filter.ShowIdealState || defaultFilter.ShowIdealState,
        ShowMetrics: filter.ShowMetrics || defaultFilter.ShowMetrics,
        ShowCriticalSuccessFactors: filter.ShowCriticalSuccessFactors || defaultFilter.ShowCriticalSuccessFactors,
        HierarchyTypeID: filter.HierarchyTypeID || defaultFilter.HierarchyTypeID,
        PeriodTypeID: filter.PeriodTypeID || defaultFilter.PeriodTypeID,
        LayoutTypeID: filter.LayoutTypeID || defaultFilter.LayoutTypeID,
        OnlyDirectlyConnectedKanbanItems: filter.OnlyDirectlyConnectedKanbanItems || defaultFilter.OnlyDirectlyConnectedKanbanItems,
        ShowKanbanActions: filter.ShowKanbanActions || defaultFilter.ShowKanbanActions
    });
}

export function convertJsonToStrategyPlannerFilter(filterJson: string): StrategyPlannerFilter {
    const defaultFilter = DefaultStrategyPlannerFilter;
    if (!filterJson || !filterJson.length) { return defaultFilter; }

    const filter = JSON.parse(filterJson) as any as StrategyPlannerFilter;

    let resultFilter = defaultFilter;
    for (const propertyName in resultFilter) {
        if (Object.prototype.hasOwnProperty.call(resultFilter, propertyName) &&
            Object.prototype.hasOwnProperty.call(filter, propertyName)) {
            resultFilter = deepAssign(resultFilter, { [propertyName]: (filter as any)[propertyName] });
        }
    }

    return resultFilter;
}

export function convertJsonToStrategyOrganizationStructureFilter(filterJson: string): StrategyOrganizationStructureFilter {
    const defaultFilter = DefaultStrategyOrganizationStructureFilter;
    if (!filterJson || !filterJson.length) { return defaultFilter; }

    const filter = JSON.parse(filterJson) as any as StrategyOrganizationStructureFilter;

    let resultFilter = defaultFilter;
    for (const propertyName in resultFilter) {
        if (Object.prototype.hasOwnProperty.call(resultFilter, propertyName) &&
            Object.prototype.hasOwnProperty.call(filter, propertyName)) {
            resultFilter = deepAssign(resultFilter, { [propertyName]: (filter as any)[propertyName] });
        }
    }

    return resultFilter;
}


type MilestoneOrProgram = entities.StrategicGoalMilestoneReport | entities.StrategicProgramReport;

export function getMilestoneChildren(
    milestone: entities.StrategicGoalMilestoneReport,
    milestones: entities.StrategicGoalMilestoneReport[],
    programs: entities.StrategicProgramReport[]
): MilestoneOrProgram[] {
    const result: MilestoneOrProgram[] = [
        ...milestones.filter(({ FatherID }) => FatherID === milestone.ID),
        ...programs.filter(({ ParentMilestoneID, FatherID }) => ParentMilestoneID === milestone.ID && !FatherID)
    ];

    for (let entityIndex = 0; entityIndex < result.length; entityIndex++) {
        const
            entity = result[entityIndex],
            isMilestone = entity.EntityType === milestone.EntityType;

        if (isMilestone) {
            result.push(
                ...milestones.filter(({ FatherID }) => FatherID === entity.ID),
                ...programs.filter(({ ParentMilestoneID, FatherID }) => ParentMilestoneID === entity.ID && !FatherID)
            );
        }
        else {
            result.push(
                ...milestones.filter(({ ParentProgramID }) => ParentProgramID === entity.ID),
                ...programs.filter(({ FatherID }) => FatherID === entity.ID)
            );
        }
    }

    return result;
}

export function getProgramChildren(
    program: entities.StrategicProgramReport,
    milestones: entities.StrategicGoalMilestoneReport[],
    programs: entities.StrategicProgramReport[]
): MilestoneOrProgram[] {
    const result: MilestoneOrProgram[] = [
        ...milestones.filter(({ ParentProgramID }) => ParentProgramID === program.ID),
        ...programs.filter(({ FatherID }) => FatherID === program.ID)
    ];

    for (let entityIndex = 0; entityIndex < result.length; entityIndex++) {
        const
            entity = result[entityIndex],
            isMilestone = entity.EntityType !== program.EntityType;

        if (isMilestone) {
            result.push(
                ...milestones.filter(({ FatherID }) => FatherID === entity.ID),
                ...programs.filter(({ ParentMilestoneID, FatherID }) => ParentMilestoneID === entity.ID && !FatherID)
            );
        }
        else {
            result.push(
                ...milestones.filter(({ ParentProgramID }) => ParentProgramID === entity.ID),
                ...programs.filter(({ FatherID }) => FatherID === entity.ID)
            );
        }
    }

    return result;
}


interface FilterableStrategyProps {
    session: entities.Session;
    filter: StrategyFilter;
    strategies: entities.NetworkArray<entities.StrategyDescriptionReport>;
}

export function filterStrategies(props: FilterableStrategyProps, includeArtificialStrategies = false) {
    const
        { filter } = props,
        strategyQueue = props.strategies.Data.filter(({ FatherID, IsArtificial }) => !FatherID && !IsArtificial),

        isPublicPage = props.session.IsPublicPageUser,
        publicPageCreatedByUserID = props.session.PublicPageCreatedByUserID;

    let strategies: entities.StrategyDescriptionReport[] = [];

    for (let strategyIndex = 0; strategyIndex < strategyQueue.length; strategyIndex++) {
        const
            strategy = strategyQueue[strategyIndex],
            substrategies = props.strategies.Data.filter(({ FatherID, IsArtificial }) => FatherID === strategy.ID && !IsArtificial),

            passesStrategyCheck = !filter.StrategyIDs.length && (filter.View !== StrategyView.STRATEGY || !strategy.FatherID) || filter.StrategyIDs.includes(strategy.ID),
            passesStatusCheck = !filter.StrategyStatusIDs.length || filter.StrategyStatusIDs.includes(strategy.StatusID) && (
                strategy.StatusID !== StrategyDescriptionStatus.PLANNING ||
                filter.ShowOnlyPersonalDrafts === filter.ShowOnlyOthersDrafts ||
                filter.ShowOnlyPersonalDrafts && (!isPublicPage && strategy.CreatorID === props.session.UserID || isPublicPage && strategy.CreatorID === publicPageCreatedByUserID) ||
                filter.ShowOnlyOthersDrafts && (!isPublicPage && strategy.CreatorID !== props.session.UserID || isPublicPage && strategy.CreatorID !== publicPageCreatedByUserID)
            ),
            parentVisible = strategies.getByID(strategy.FatherID);

        if ((passesStrategyCheck || parentVisible) &&
            (passesStatusCheck || passesStrategyCheck && strategy.StatusID !== StrategyDescriptionStatus.ARCHIVED)) {
            strategies.push(strategy);
        }

        strategyQueue.splice(strategyIndex + 1, 0, ...substrategies);
    }

    if (!filter.StrategyIDs.length && includeArtificialStrategies) {
        const artificialStrategies = props.strategies.Data.filter(({ FatherID, IsArtificial }) => !FatherID && IsArtificial);
        strategies = [...strategies, ...artificialStrategies];
    }

    return strategies;
}

interface FilterableGoalProps extends FilterableStrategyProps {
    strategicGoals: entities.NetworkArray<entities.StrategicGoalReport>;
}

export function filterGoals(props: FilterableGoalProps, onlyDirectlyConnected = false, includeArtificialGoals = false) {
    const
        { filter } = props,
        strategies = filterStrategies(props);

    let goals = props.strategicGoals.Data.filter((goal) => {
        if (goal.IsArtificial) { return false; }
        if (filter.StrategyIDs.length && !strategies.getByID(goal.StrategyDescriptionID!)) { return false; }

        if (onlyDirectlyConnected) {
            if (filter.ResponsibleCompanyFunctionIDs.length) { return filter.ResponsibleCompanyFunctionIDs.includes(goal.CompanyFunctionID!); }
            if (filter.ResponsibleVirtualTeamIDs.length) { return filter.ResponsibleVirtualTeamIDs.includes(goal.CompanyVirtualTeamID!); }
            if (filter.ResponsibleUserIDs.length) { return filter.ResponsibleUserIDs.includes(goal.OwnerID!); }
        }

        return true;
    });

    if (includeArtificialGoals) {
        const artificialGoals = props.strategicGoals.Data.filter(({ IsArtificial }) => IsArtificial);
        goals = [...goals, ...artificialGoals];
    }

    return goals;
}

interface FilterableProgramProps extends FilterableGoalProps {
    strategicGoalMilestones: entities.NetworkArray<entities.StrategicGoalMilestoneReport>;
    strategicPrograms: entities.NetworkArray<entities.StrategicProgramReport>;
    strategyExecutionCustomFields: entities.NetworkArray<entities.StrategyExecutionCustomFieldReport>;
    users: entities.NetworkArray<entities.UserLocal>;
    companyVirtualTeamMembers: entities.NetworkArray<entities.CompanyVirtualTeamMember>;
}

function isMilestoneVisible(props: FilterableProgramProps, milestone: entities.StrategicGoalMilestoneReport, goals: entities.StrategicGoalReport[], children: MilestoneOrProgram[]) {
    if (children.some(({ EntityType }) => EntityType === MILESTONE_ENTITY_TYPE)) { return true; }
const statusUpdates = milestone.StatusUpdates.Data.map(({ UpdateDate }) => +UpdateDate);
    const maxUpdateDate = Math.max(...statusUpdates); 
    const
        { filter } = props,
        goalID = milestone.GoalID || milestone.ArtificialGoalID,
        goal = goals.getByID(goalID),

        isPublicPage = props.session.IsPublicPageUser,
        publicPageCreatedByUserID = props.session.PublicPageCreatedByUserID,

        programs = children.filter(({ EntityType }) => EntityType === PROGRAM_ENTITY_TYPE) as unknown as entities.StrategicProgramReport[],
        responsibleUserIDs = [
            ...milestone.OwnerUserID ? [milestone.OwnerUserID] : [],
            ...milestone.SecondaryOwners.map(({ UserID }) => UserID),
            ...filter.View === StrategyView.ROADMAP || filter.View === StrategyView.MAP ?
                [
                    ...milestone.Metrics.Data.map(({ OwnerID }) => OwnerID),
                    ...programs.map(({ OwnerUserID, SecondaryOwners }) => [OwnerUserID, ...SecondaryOwners.map(({ UserID }) => UserID)]).flatten()
                ] :
                []
        ].unique(),
        requireChildPrograms = !!filter.RiskLevelIDs.length || !!filter.ProgramCompanyFunctionIDs.length || !!filter.ProgramVirtualTeamIDs.length ||
            filter.ProgramCustomFieldOptionIDs.some(optionID =>
                props.strategyExecutionCustomFields.Data.some(({ IsGoalMilestoneField, Options }) => !IsGoalMilestoneField && Options.getByID(optionID))
            ),

        matchesTextFilter = !filter.TextFilter ||
            !!children.length ||
            milestone.Name.toLowerCase().includes(filter.TextFilter.toLowerCase()) ||
            milestone.Metrics.Data.some(({ Name }) => Name.toLowerCase().includes(filter.TextFilter.toLowerCase()));  
    const objectiveNotUpdatedTimestamp = filter?.ObjectiveNotUpdatedPeriodStart ? new Date(filter?.ObjectiveNotUpdatedPeriodStart).getTime() : null;
    const objectiveNotUpdatedEndTimestamp = filter?.ObjectiveNotUpdatedPeriodEnd ? new Date(filter?.ObjectiveNotUpdatedPeriodEnd).getTime() : null;
  

    return (!goalID || goal) &&
        matchesTextFilter &&
        (!filter.StrategyIDs.length || goalID && goal) &&
        (!filter.AchievabilityIDs.length || filter.AchievabilityIDs.includes(milestone.AchievabilityID)) &&
        (!requireChildPrograms || programs.some(({ ParentMilestoneID }) => ParentMilestoneID === milestone.ID)) &&
        (
            (!filter.ResponsibleUserIDs.length || responsibleUserIDs.some(userID => filter.ResponsibleUserIDs.includes(userID))) &&
            (
                !filter.ResponsibleCompanyFunctionIDs.length && !filter.ResponsibleVirtualTeamIDs.length ||
                filter.ResponsibleCompanyFunctionIDs.includes(milestone.CompanyFunctionID!) ||
                filter.ResponsibleVirtualTeamIDs.includes(milestone.CompanyVirtualTeamID!) ||
                milestone.TeamLinks.some(({ CompanyFunctionID, VirtualTeamID }) =>
                    CompanyFunctionID && filter.ResponsibleCompanyFunctionIDs.includes(CompanyFunctionID) ||
                    VirtualTeamID && filter.ResponsibleVirtualTeamIDs.includes(VirtualTeamID)
                )
            ) &&
            (
                !filter.MilestoneCompanyFunctionIDs.length && !filter.MilestoneVirtualTeamIDs.length ||
                filter.MilestoneCompanyFunctionIDs.includes(milestone.CompanyFunctionID!) ||
                filter.MilestoneVirtualTeamIDs.includes(milestone.CompanyVirtualTeamID!) ||
                milestone.TeamLinks.some(({ CompanyFunctionID, VirtualTeamID }) =>
                    CompanyFunctionID && filter.MilestoneCompanyFunctionIDs.includes(CompanyFunctionID) ||
                    VirtualTeamID && filter.MilestoneVirtualTeamIDs.includes(VirtualTeamID)
                )
            )
        ) &&
        (!filter.MilestoneStatusIDs.length || filter.MilestoneStatusIDs.includes(milestone.StatusID) && (
            milestone.StatusID !== StrategicGoalMilestoneStatus.DRAFT ||
            filter.ShowOnlyPersonalDrafts === filter.ShowOnlyOthersDrafts ||
            filter.ShowOnlyPersonalDrafts && (
                !isPublicPage && (milestone.OwnerUserID === props.session.UserID || milestone.CanUserMakeSecondaryStatusUpdate) ||
                isPublicPage && (milestone.OwnerUserID === publicPageCreatedByUserID || milestone.SecondaryOwners.some(({ UserID }) => UserID === publicPageCreatedByUserID))
            ) ||
            filter.ShowOnlyOthersDrafts && (
                !isPublicPage && milestone.OwnerUserID !== props.session.UserID && !milestone.CanUserMakeSecondaryStatusUpdate ||
                isPublicPage && (milestone.OwnerUserID !== publicPageCreatedByUserID || !milestone.SecondaryOwners.some(({ UserID }) => UserID === publicPageCreatedByUserID))
            )
        )) &&
        (
            !filter.MilestoneCustomFieldOptionIDs.length ||
            props.strategyExecutionCustomFields.Data
                .filter(({ IsGoalMilestoneField, Options }) => IsGoalMilestoneField && Options.some(({ ID }) => filter.MilestoneCustomFieldOptionIDs.includes(ID)))
                .map(field => ({ ...field, Options: field.Options.filter(({ ID }) => filter.MilestoneCustomFieldOptionIDs.includes(ID)) }))
                .every(({ IsStrategicProgramField, Options }) =>
                    Options.some(option => milestone.CustomFieldLinks.some(({ CustomFieldOptionID }) => CustomFieldOptionID === option.ID)) ||
                    IsStrategicProgramField && programs.length
                )
        ) &&
        (
            !filter.MilestoneCustomFieldDateFilters.length ||
            filter.MilestoneCustomFieldDateFilters.every(dateFilter =>
                !dateFilter.CustomPeriodStart && !dateFilter.CustomPeriodEnd ||
                milestone.CustomFieldLinks.some(({ CustomFieldID, CustomFieldDateValue }) =>
                    CustomFieldID === dateFilter.CustomFieldID && CustomFieldDateValue &&
                    (!dateFilter.CustomPeriodStart || +CustomFieldDateValue >= +dateFilter.CustomPeriodStart) &&
                    (!dateFilter.CustomPeriodEnd || +CustomFieldDateValue <= +dateFilter.CustomPeriodEnd)
                ) ||
                programs.length && filter.ProgramCustomFieldDateFilters.some(({ CustomFieldID }) => CustomFieldID === dateFilter.CustomFieldID)
            )
        ) &&
        (
            (!objectiveNotUpdatedTimestamp || +maxUpdateDate < +objectiveNotUpdatedTimestamp)
            ||
            (!objectiveNotUpdatedEndTimestamp || +maxUpdateDate > +objectiveNotUpdatedEndTimestamp)
        ) &&
        (
            !filter.CustomPeriodStart && !filter.CustomPeriodEnd ||
            (!filter.CustomPeriodStart || +milestone.AchievedByDate > +filter.CustomPeriodStart) &&
            (!filter.CustomPeriodEnd || +milestone.StartDate < +filter.CustomPeriodEnd)
        );
}

type SecondaryOwner = entities.StrategicGoalMilestoneSecondaryOwner | entities.StrategicProgramSecondaryOwner;

function isProgramVisible(props: FilterableProgramProps, program: entities.StrategicProgramReport, goals: entities.StrategicGoalReport[], children: MilestoneOrProgram[]) {
    const
        { filter } = props,

        isPublicPage = props.session.IsPublicPageUser,
        publicPageCreatedByUserID = props.session.PublicPageCreatedByUserID,

        milestone = props.strategicGoalMilestones.Data.getByID(program.ParentMilestoneID),
        goalID = milestone?.GoalID || milestone?.ArtificialGoalID,
        goal = goals.getByID(goalID); 
    const actionNotUpdatedTimestamp = filter?.ActionNotUpdatedPeriodStart  ;
    const actionNotUpdatedEndTimestamp = filter?.ActionNotUpdatedPeriodEnd; 
    if (!milestone) { return false; }

    const
        hasChildProgram = children.some(({ EntityType }) => EntityType === PROGRAM_ENTITY_TYPE),
        filterByOwner = filter.ResponsibleUserIDs.length > 0,
        filterByProgramTeam = filter.ProgramCompanyFunctionIDs.length > 0;

    let secondaryOwners: number[] = [];

    if (program.SecondaryStatusUpdateTypeID !== StrategyExecutionSecondaryStatusUpdateType.NONE && (filterByOwner || filterByProgramTeam && !hasChildProgram)) {
        secondaryOwners = program.SecondaryOwners.map(({ UserID }) => UserID);
    }

    const
        matchesTextFilter = !filter.TextFilter || !!children.length || program.Name.toLowerCase().includes(filter.TextFilter.toLowerCase()),
        matchesStrategyFilter = !filter.StrategyIDs.length || goal || filter.View === StrategyView.KANBAN,
        matchesResponsibleTeamFilter = !filter.ResponsibleCompanyFunctionIDs.length || filter.ResponsibleCompanyFunctionIDs.includes(program.CompanyFunctionID!),
        matchesOwnerFilter = !filterByOwner ||
            filter.ResponsibleUserIDs.includes(program.OwnerUserID) ||
            secondaryOwners.some(userID => filter.ResponsibleUserIDs.includes(userID)) ||
            children.some(child =>
                filter.ResponsibleUserIDs.includes(child.OwnerUserID!) ||
                child.SecondaryOwners.some((owner: SecondaryOwner) => filter.ResponsibleUserIDs.includes(owner.UserID)) ||
                (filter.View === StrategyView.ROADMAP || filter.View === StrategyView.MAP) && child.EntityType === MILESTONE_ENTITY_TYPE &&
                child.Metrics.Data.some(({ OwnerID }) => filter.ResponsibleUserIDs.includes(OwnerID))
            ),
        matchesRiskLevelFilter = !filter.RiskLevelIDs.length || filter.RiskLevelIDs.includes(program.RiskLevelID),
        matchesStatusFilter = !filter.ProgramStatusIDs.length || filter.ProgramStatusIDs.includes(program.StatusID) && (
            program.StatusID !== StrategicProgramStatus.DRAFT ||
            filter.ShowOnlyPersonalDrafts === filter.ShowOnlyOthersDrafts ||
            filter.ShowOnlyPersonalDrafts && (!isPublicPage && program.OwnerUserID === props.session.UserID || isPublicPage && program.OwnerUserID === publicPageCreatedByUserID) ||
            filter.ShowOnlyOthersDrafts && (!isPublicPage && program.OwnerUserID !== props.session.UserID || isPublicPage && program.OwnerUserID !== publicPageCreatedByUserID)
        );

    if (!matchesTextFilter || !matchesStrategyFilter || !matchesResponsibleTeamFilter || !matchesOwnerFilter || !matchesRiskLevelFilter || !matchesStatusFilter) {
        return false;
    }

    if (!hasChildProgram) {
        const
            owners = filterByProgramTeam ?
                [program.OwnerUserID, ...secondaryOwners].unique().map(userID => props.users.Data.getByID(userID)).filter(v => v) as unknown as entities.UserLocal[] :
                [],
            matchesProgramTeamFilter = !filterByProgramTeam && !filter.ProgramVirtualTeamIDs.length ||
                filterByProgramTeam && owners.some(({ CompanyFunctionID }) => filter.ProgramCompanyFunctionIDs.includes(CompanyFunctionID)) ||
                filter.ProgramVirtualTeamIDs.length && props.companyVirtualTeamMembers.Data.some(({ UserID, TeamID }) =>
                    UserID === program.OwnerUserID && filter.ProgramVirtualTeamIDs.includes(TeamID)
                ),
            matchesCustomFieldFilter = !filter.ProgramCustomFieldOptionIDs.length || props.strategyExecutionCustomFields.Data
                .filter(({ IsStrategicProgramField, Options }) => IsStrategicProgramField && Options.some(({ ID }) => filter.ProgramCustomFieldOptionIDs.includes(ID)))
                .map(field => ({ ...field, Options: field.Options.filter(({ ID }) => filter.ProgramCustomFieldOptionIDs.includes(ID)) }))
                .every(({ IsGoalMilestoneField, Options }) =>
                    Options.some(option => program.CustomFieldLinks.some(({ CustomFieldOptionID }) => CustomFieldOptionID === option.ID)) ||
                    children.some(({ EntityType }) => IsGoalMilestoneField && EntityType === MILESTONE_ENTITY_TYPE)
            ),
            matchesActionNotUpdatedStart = !actionNotUpdatedTimestamp || program.LastUpdateDate >= actionNotUpdatedTimestamp ,
            matchesActionNotUpdatedEnd = !actionNotUpdatedEndTimestamp || program.LastUpdateDate <= actionNotUpdatedEndTimestamp,
            matchesCustomDateFilter = !filter.ProgramCustomFieldDateFilters.length ||
                filter.ProgramCustomFieldDateFilters.every(dateFilter =>
                    !dateFilter.CustomPeriodStart && !dateFilter.CustomPeriodEnd ||
                    program.CustomFieldLinks.some(({ CustomFieldID, CustomFieldDateValue }) =>
                        CustomFieldID === dateFilter.CustomFieldID && CustomFieldDateValue &&
                        (!dateFilter.CustomPeriodStart || +CustomFieldDateValue >= +dateFilter.CustomPeriodStart) &&
                        (!dateFilter.CustomPeriodEnd || +CustomFieldDateValue <= +dateFilter.CustomPeriodEnd)
                    ) ||
                    children.some(({ EntityType }) =>
                        EntityType === MILESTONE_ENTITY_TYPE && filter.MilestoneCustomFieldDateFilters.some(({ CustomFieldID }) => CustomFieldID === dateFilter.CustomFieldID)
                    )
                );

        return matchesProgramTeamFilter && matchesCustomFieldFilter && matchesCustomDateFilter && matchesActionNotUpdatedStart && matchesActionNotUpdatedEnd;
    }

    return true;
}

function filterMilestoneHierarchy(props: FilterableProgramProps, onlyDirectlyConnected = false, includeArtificialGoals = false): MilestoneOrProgram[] {
    const
        goals = filterGoals(props, onlyDirectlyConnected, includeArtificialGoals),
        hierarchy: MilestoneOrProgram[] = props.strategicGoalMilestones.Data
            .filter(({ FatherID, GoalID }) => !FatherID && (!GoalID || goals.getByID(GoalID)))
            .map(milestone => [milestone, ...getMilestoneChildren(milestone, props.strategicGoalMilestones.Data, props.strategicPrograms.Data)])
            .flatten()
            .reverse(),
        milestones: entities.StrategicGoalMilestoneReport[] = [],
        programs: entities.StrategicProgramReport[] = [];

    for (let entityIndex = 0; entityIndex < hierarchy.length; entityIndex++) {
        const
            entity = hierarchy[entityIndex],
            children = entity.EntityType === MILESTONE_ENTITY_TYPE ? getMilestoneChildren(entity, milestones, programs) : getProgramChildren(entity, milestones, programs);

        if (entity.EntityType === MILESTONE_ENTITY_TYPE && isMilestoneVisible(props, entity, goals, children)) {
            milestones.push(entity);
        }
        else if (entity.EntityType === PROGRAM_ENTITY_TYPE && isProgramVisible(props, entity, goals, children)) {
            programs.push(entity);
        }
    }

    const results = milestones
        .filter(({ FatherID }) => !FatherID)
        .map(milestone => [milestone, ...getMilestoneChildren(milestone, milestones, programs)])
        .flatten()
        .groupBy(entity => `${entity.EntityType}_${entity.ID}`)
        .map(duplicates => duplicates.first());

    return results;
}

export function filterMilestones(props: FilterableProgramProps, onlyDirectlyConnected = false, includeArtificialGoals = false): entities.StrategicGoalMilestoneReport[] {
    const
        hierarchy = filterMilestoneHierarchy(props, onlyDirectlyConnected, includeArtificialGoals),
        milestones = hierarchy.filter(({ EntityType }) => EntityType === MILESTONE_ENTITY_TYPE).sortBy('Sort') as unknown as entities.StrategicGoalMilestoneReport[];

    return milestones;
}

export function filterPrograms(props: FilterableProgramProps, onlyDirectlyConnected = false, includeArtificialGoals = false) {
    const
        hierarchy = filterMilestoneHierarchy(props, onlyDirectlyConnected, includeArtificialGoals),
        programs = hierarchy.filter(({ EntityType }) => EntityType === PROGRAM_ENTITY_TYPE) as unknown as entities.StrategicProgramReport[];

    return programs;
}

export function filterMetrics(props: FilterableStrategyProps, metrics: entities.GoalMetricReport[]): entities.GoalMetricReport[] {
    const { filter } = props;

    return metrics.filter(metric =>
        (!filter.TextFilter || metric.Name.toLowerCase().includes(filter.TextFilter.toLowerCase())) &&
        (!filter.ResponsibleUserIDs.length || filter.ResponsibleUserIDs.includes(metric.OwnerID)) &&
        (!filter.ProgramCompanyFunctionIDs.length && !filter.ProgramVirtualTeamIDs.length || !!metric.MilestoneID || !!metric.ProgramID)
    );
}

function isEmpty(obj: any) {
    if (obj === null || obj === undefined) {
        return true;
    }

    if (obj.length === 0) {
        return true;
    }

    if (obj.length > 0) {
        return false;
    }

    for (const prop in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, prop)) {
            return false;
        }
    }

    return true;
}

export function isStrategyFilterEmpty(filter: StrategyFilter, ignoredFilters: string[] = []) {
    type FilterKey = keyof typeof filter;
    const excludedPropertyNames = [...ignoredFilters, 'View', 'Initialized'];

    for (const propertyName in filter) {
        if (Object.prototype.hasOwnProperty.call(filter, propertyName) && !excludedPropertyNames.includes(propertyName)) {
            const value = filter[propertyName as FilterKey] as any;

            if (!isEmpty(value) || value === true) {
                return false;
            }
        }
    }

    return true;
}

function getMilestoneOrProgramDirectParents(
    parentMilestoneID: number | null,
    parentProgramID: number | null,
    milestones: entities.StrategicGoalMilestoneReport[],
    programs: entities.StrategicProgramReport[]
): MilestoneOrProgram[] {
    const parents: MilestoneOrProgram[] = [];

    while (parentMilestoneID || parentProgramID) {
        const
            parentMilestone: entities.StrategicGoalMilestoneReport | null = parentMilestoneID ? milestones.getByID(parentMilestoneID) || null : null,
            parentProgram: entities.StrategicProgramReport | null = parentProgramID ? programs.getByID(parentProgramID) || null : null;

        if (parentMilestone) {
            parents.push(parentMilestone);
            parentMilestoneID = parentMilestone.FatherID;
            parentProgramID = parentMilestone.ParentProgramID;
        }

        if (parentProgram) {
            parents.push(parentProgram);
            parentMilestoneID = parentProgram.FatherID ? null : parentProgram.ParentMilestoneID;
            parentProgramID = parentProgram.FatherID;
        }

        if (!parentMilestone && !parentProgram) {
            parentMilestoneID = null;
            parentProgramID = null;
        }
    }

    return parents;
}

export function getProgramDirectParents(
    program: entities.StrategicProgramReport | null | undefined,
    milestones: entities.StrategicGoalMilestoneReport[],
    programs: entities.StrategicProgramReport[]
): MilestoneOrProgram[] {
    if (!program) { return []; }

    const
        parentMilestoneID: number | null = program.FatherID ? null : program.ParentMilestoneID,
        parentProgramID: number | null = program.FatherID;

    return getMilestoneOrProgramDirectParents(parentMilestoneID, parentProgramID, milestones, programs);
}

export function getMilestoneDirectParents(
    milestone: entities.StrategicGoalMilestoneReport | null | undefined,
    milestones: entities.StrategicGoalMilestoneReport[],
    programs: entities.StrategicProgramReport[]
): MilestoneOrProgram[] {
    if (!milestone) { return []; }

    const
        parentMilestoneID: number | null = milestone.FatherID,
        parentProgramID: number | null = milestone.ParentProgramID;

    return getMilestoneOrProgramDirectParents(parentMilestoneID, parentProgramID, milestones, programs);
}

export function getMilestoneOrProgramConnections(
    parent: MilestoneOrProgram,
    milestones: entities.StrategicGoalMilestoneReport[],
    programs: entities.StrategicProgramReport[]
): MilestoneOrProgram[] {
    if (parent.ProgressCalculationMethodID === StrategyProgressCalculationMethod.MANUAL_STATUS_UPDATE) { return []; }

    const
        isParentMilestone = parent.EntityType === MILESTONE_ENTITY_TYPE,
        connectionPropertyName = isParentMilestone ? 'ConnectedToMilestoneID' : 'ConnectedToProgramID',
        listDirectChildren = parent.ProgressCalculationSourceID === StrategyProgressCalculationSource.DIRECT_CHILDREN;

    return [
        ...milestones.filter((milestone) => {
            const isConnectedToParent = !listDirectChildren ?
                milestone[connectionPropertyName] === parent.ID :
                isParentMilestone ?
                    milestone.FatherID === parent.ID && !milestone.ParentProgramID :
                    !milestone.FatherID && milestone.ParentProgramID === parent.ID;

            if (milestone.ProgressCalculationMethodID === StrategyProgressCalculationMethod.MANUAL_STATUS_UPDATE || !isConnectedToParent) {
                return false;
            }

            switch (parent.ProgressCalculationMethodID) {
                case StrategyProgressCalculationMethod.OPEN_ITEMS_AVERAGE_PROGRESS:
                    return milestone.StatusID === StrategicGoalMilestoneStatus.ACTIVE && (milestone.SuccessCalculationCurrentPercentage || 0) < 100;

                case StrategyProgressCalculationMethod.INCOMPLETE_ITEMS_AVERAGE_PROGRESS:
                case StrategyProgressCalculationMethod.ITEM_COMPLETION_PERCENTAGE:
                    return milestone.StatusID !== StrategicGoalMilestoneStatus.DRAFT;

                default:
                    return false;
            }
        }),
        ...programs.filter((program) => {
            const isConnectedToParent = !listDirectChildren ?
                program[connectionPropertyName] === parent.ID :
                isParentMilestone ?
                    program.ParentMilestoneID === parent.ID && !program.FatherID :
                    program.FatherID === parent.ID;

            if (program.NoProgressTracking || !isConnectedToParent) {
                return false;
            }

            switch (parent.ProgressCalculationMethodID) {
                case StrategyProgressCalculationMethod.OPEN_ITEMS_AVERAGE_PROGRESS:
                    return program.StatusID === StrategicProgramStatus.OPEN && program.Progress < 100;

                case StrategyProgressCalculationMethod.INCOMPLETE_ITEMS_AVERAGE_PROGRESS:
                case StrategyProgressCalculationMethod.ITEM_COMPLETION_PERCENTAGE:
                    return program.StatusID !== StrategicProgramStatus.DRAFT;

                default:
                    return false;
            }
        })
    ];
}

export function isMetricValid(metric: entities.GoalMetricReport): boolean {
    if (!metric.Name && !metric.IndicatorID) { return false; }
    if (metric.FatherID !== null && !metric.FatherID) { return false; }
    if (!metric.UseIncreasingDirection && metric.StartValue === null) { return false; }
    if (!metric.UseIncreasingDirection && metric.GoalValueTypeID === GoalMetricValueType.DEFINED_BY_USER) { return false; }

    return true;
}

export interface MetricProgress {
    Period: Date;
    Actual: number | null;
    Goal: number | null;
    Progress: number | null;
}

export function calculateMetricProgress(metric: entities.GoalMetricReport, entries: entities.PerformanceIndicatorDataBase[], date: Date | null = null): MetricProgress {
    const
        period = new Period(metric.PeriodTypeID, date),
        data = entries.filter(({ Date }) => +Date <= +period),

        actuals = data.filter(({ Actual, ActualSourceID }) => Actual || ActualSourceID),
        startValue = metric.GoalValueTypeID !== GoalMetricValueType.DEFINED_BY_USER && metric.StartValue !== null ? metric.StartValue : 0,
        actual = metric.GoalValueTypeID !== GoalMetricValueType.ACHIEVED_ONCE ?
            actuals.last() ? actuals.last().Actual : null :
            data.length ? data.sum('Actual') : null,

        goalEntry = actuals.last() || data.last();

    let goal: number | null = null;

    if (metric.GoalValueTypeID === GoalMetricValueType.DEFINED_BY_USER) {
        if (goalEntry) { goal = goalEntry.Goal; }
    }
    else if (metric.GoalValue !== null && (!metric.UseIncreasingDirection || !!metric.GoalValue)) {
        goal = metric.GoalValue;
    }

    return {
        Period: metric.GoalValueTypeID === GoalMetricValueType.ACHIEVED_ONCE ? period : actuals.last()?.Date || period,
        Actual: actual,
        Goal: goal,
        Progress: goal && actual !== null && goal - startValue !== 0 ? (actual - startValue) / (goal - startValue) * 100 : null
    };
}

interface PublishableProps {
    strategies: entities.NetworkArray<entities.StrategyDescriptionReport>;
    strategicGoals: entities.NetworkArray<entities.StrategicGoalReport>;
    strategicGoalMilestones: entities.NetworkArray<entities.StrategicGoalMilestoneReport>;
    strategicPrograms: entities.NetworkArray<entities.StrategicProgramReport>;
}

type PublishableEntity = entities.StrategyDescriptionReport | entities.StrategicGoalReport | entities.StrategicGoalMilestoneReport | entities.StrategicProgramReport;

export function useStrategyPublishing(props: PublishableProps, entity: PublishableEntity | undefined): boolean {
    if (!entity) { return false; }  
    switch (entity.EntityType) {
        case defaults.StrategyDescriptionReport.EntityType:
            return entity.PublishingTypeID === StrategyPublishingType.WITH_FULL_REVIEW;

        case defaults.StrategicGoalReport.EntityType:
            return entity.StrategyDescriptionID ? useStrategyPublishing(props, props.strategies.Data.getByID(entity.StrategyDescriptionID)) : false;

        case defaults.StrategicGoalMilestoneReport.EntityType:
            return entity.GoalID || entity.FatherID || entity.ParentProgramID ?
                !!entity.GoalID && useStrategyPublishing(props, props.strategicGoals.Data.getByID(entity.GoalID)) ||
                !!entity.FatherID && useStrategyPublishing(props, props.strategicGoalMilestones.Data.getByID(entity.FatherID)) ||
                !!entity.ParentProgramID && useStrategyPublishing(props, props.strategicPrograms.Data.getByID(entity.ParentProgramID)) :
                false;

        case defaults.StrategicProgramReport.EntityType:
            return entity.ParentMilestoneID || entity.FatherID ?
                !!entity.ParentMilestoneID && useStrategyPublishing(props, props.strategicGoalMilestones.Data.getByID(entity.ParentMilestoneID)) ||
                !!entity.FatherID && useStrategyPublishing(props, props.strategicPrograms.Data.getByID(entity.FatherID)) :
                false;

        default:
            return false;
    }
}

interface NamingTypeProps extends PublishableProps {
    generalSettings: entities.GeneralSettings;
}

export function getNamingTypeID(props: NamingTypeProps, entity: PublishableEntity | undefined): StrategyNamingType {
    if (!entity) { return props.generalSettings.DefaultStrategyNamingTypeID; }

    switch (entity.EntityType) {
        case defaults.StrategyDescriptionReport.EntityType:
            return entity.StrategyNamingTypeID;

        case defaults.StrategicGoalReport.EntityType:
            return getNamingTypeID(props, props.strategies.Data.getByID(entity.StrategyDescriptionID));

        case defaults.StrategicGoalMilestoneReport.EntityType:
            if (entity.GoalID) { return getNamingTypeID(props, props.strategicGoals.Data.getByID(entity.GoalID)); }
            if (entity.FatherID) { return getNamingTypeID(props, props.strategicGoalMilestones.Data.getByID(entity.FatherID)); }
            if (entity.ParentProgramID) { return getNamingTypeID(props, props.strategicPrograms.Data.getByID(entity.ParentProgramID)); }

            return props.generalSettings.DefaultStrategyNamingTypeID;

        case defaults.StrategicProgramReport.EntityType:
            if (entity.ParentMilestoneID) { return getNamingTypeID(props, props.strategicGoalMilestones.Data.getByID(entity.ParentMilestoneID)); }
            if (entity.FatherID) { return getNamingTypeID(props, props.strategicPrograms.Data.getByID(entity.FatherID)); }

            return props.generalSettings.DefaultStrategyNamingTypeID;

        default:
            return props.generalSettings.DefaultStrategyNamingTypeID;
    }
}

interface AggregationValidationProps {
    strategicGoalMilestones: entities.NetworkArray<entities.StrategicGoalMilestoneReport>;
    strategicPrograms: entities.NetworkArray<entities.StrategicProgramReport>;
}

export function forceNonManualProgressionMethod(milestone: entities.StrategicGoalMilestoneReport, props: AggregationValidationProps): boolean {
    const parents = getMilestoneDirectParents(milestone, props.strategicGoalMilestones.Data, props.strategicPrograms.Data);
    if (!parents.length) { return false; }

    return parents.some(parent =>
        parent.EntityType === MILESTONE_ENTITY_TYPE &&
        parent.ProgressCalculationMethodID !== StrategyProgressCalculationMethod.MANUAL_STATUS_UPDATE
    );
}
